import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import ReactTooltip from 'react-tooltip';
import { isEmpty } from 'lodash';
import {
  ErrorMessage,
  Icon,
  SelectDropdown,
  TextInput,
} from '@gsa/afp-component-library';
import PredefinedValueTable from './predefined-values-table';

/**
 * Determines which fields should be updated based on association type and input type.
 * This function normalizes form data to ensure only relevant fields are sent to the API.
 *
 * @param {Object} rawFormData - The raw form data from the equipment association form
 * @param {string} rawFormData.associationTypeCode - The association type code ('O', 'S', 'M')
 * @param {string} rawFormData.inputTypeCode - The input type code ('N', 'R', 'P', 'C', 'F')
 * @param {string} rawFormData.associationText - The association text/description
 * @param {number} rawFormData.lowerBound - The lower bound value for numerical/range inputs
 * @param {number} rawFormData.upperBound - The upper bound value for range inputs
 * @param {string} rawFormData.unitCode - The unit code for numerical/range inputs
 * @param {string} rawFormData.criteriaCode - The criteria code for numerical inputs
 * @param {*} rawFormData.preDefinedValue - The predefined value for predefined inputs
 * @returns {Object} An object containing only the fields that should be updated
 */
export const determineUpdatableFields = (rawFormData) => {
  const typeCode = rawFormData?.associationTypeCode;
  const inputTypeCode = rawFormData?.inputTypeCode;

  // Optional association type - clear all specification fields
  if (typeCode === 'O') {
    return {
      associationText: null,
      inputTypeCode: null,
      lowerBound: null,
      upperBound: null,
      unitCode: null,
      preDefinedValue: null,
      associationTypeCode: typeCode,
    };
  }

  // Numerical input
  if (inputTypeCode === 'N') {
    return {
      associationText: rawFormData?.associationText,
      inputTypeCode,
      lowerBound:
        rawFormData?.criteriaCode === 'ANY' ? null : rawFormData?.lowerBound,
      upperBound: null,
      unitCode: rawFormData?.unitCode,
      criteriaCode: rawFormData?.criteriaCode,
      preDefinedValue: null,
      associationTypeCode: typeCode,
    };
  }

  // Range input
  if (inputTypeCode === 'R') {
    return {
      associationText: rawFormData?.associationText,
      inputTypeCode,
      lowerBound: rawFormData?.lowerBound,
      upperBound: rawFormData?.upperBound,
      unitCode: rawFormData?.unitCode,
      criteriaCode: null,
      preDefinedValue: null,
      associationTypeCode: typeCode,
    };
  }

  // Predefined input
  if (inputTypeCode === 'P') {
    return {
      associationText: rawFormData?.associationText,
      inputTypeCode,
      lowerBound: null,
      upperBound: null,
      unitCode: null,
      criteriaCode: null,
      preDefinedValue: rawFormData?.preDefinedValue,
      associationTypeCode: typeCode,
    };
  }

  // Comply 'C', Free text 'F', or default case for unknown input types
  return {
    associationText: rawFormData?.associationText,
    inputTypeCode,
    lowerBound: null,
    upperBound: null,
    unitCode: null,
    criteriaCode: null,
    preDefinedValue: null,
    associationTypeCode: typeCode,
  };
};

/**
 * AssociationSpecificationFields Component
 *
 * This component encapsulates the common specification fields used in both:
 * - equipment-code-association-form.jsx (Equipment Code -> Standard Item associations)
 * - equipment-association-form.jsx (Standard Item -> Equipment Code associations)
 *
 * It includes:
 * - Association Type dropdown (controls visibility of specification fields)
 * - Minimum specs for Standard association
 *
 * @param {Object} props - Component props
 * @param {Object} props.control - React Hook Form control object
 * @param {Object} props.errors - Form validation errors
 * @param {Function} props.setValue - React Hook Form setValue function
 * @param {Object} props.equipmentAssociation - Current association data for edit
 * @param {Array} props.multipleOptions - Standard codes for dropdown options
 * @param {boolean} props.isStandardItemInBetweenBidPeriods
 * @param {boolean} props.canPartialEdit
 * @param {boolean} props.isInContractYear
 * @param {boolean} props.isNew - Whether this is add or edit form
 */
const AssociationSpecificationFields = ({
  control,
  errors,
  setValue,
  equipmentAssociation = {},
  multipleOptions = [],
  isStandardItemInBetweenBidPeriods = false,
  canPartialEdit = false,
  isInContractYear = false,
  isNew = true,
}) => {
  const inputTypeCode = equipmentAssociation?.inputType?.code;
  const unitCode = equipmentAssociation?.unitCode;

  const getMultipleOptions = (type, uniqueKey) => {
    return [
      { label: '- Select -', value: '' },
      ...multipleOptions.filter(
        (o) => o.type === type && o.uniqueKey === uniqueKey,
      ),
    ];
  };

  // Dropdown options
  const minimumOptions = getMultipleOptions('standardsCodeModel', 'inputType');
  const minimumOptionsForPreviousYear = minimumOptions.filter(
    (o) => o.value === '' || o.value === 'F' || o.value === inputTypeCode,
  );
  const getMinimumOptions = () => {
    if (!isNew && (canPartialEdit || isInContractYear))
      return minimumOptionsForPreviousYear;
    return minimumOptions;
  };

  // State management
  const [showSpec, setShowSpec] = useState(
    equipmentAssociation?.associationType?.code === 'S',
  );
  const [isNumerical, setIsNumerical] = useState(inputTypeCode === 'N');
  const [isNumericalRange, setIsNumericalRange] = useState(
    inputTypeCode === 'R',
  );
  const [isPredefinedValues, setIsPredefinedValues] = useState(
    inputTypeCode === 'P',
  );
  const [criteriaValue, setCriteriaValue] = useState(
    equipmentAssociation?.criteriaCode ?? '',
  );

  const [predefinedValues, setPredefinedValues] = useState([]);
  const [deletedRows, setDeletedRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [newRows, setNewRows] = useState([]);

  // Helper functions
  const renderRequiredComponent = (value, additionStyle = '') => {
    return (
      <h2 aria-label={value} className={`font-sans-md ${additionStyle}`}>
        {value} <span className="usa-hint usa-hint--required">*</span>
      </h2>
    );
  };

  const isUnitCodeSet = () => unitCode !== null && unitCode !== '';

  const renderBoundValue = (name, defaultValue) => {
    return (
      <Controller
        name={name}
        defaultValue={defaultValue}
        control={control}
        render={({ field: { onChange, value } }) => (
          <TextInput
            type="number"
            data-testid={`minimum-number-${name}`}
            aria-label="Numerical value"
            hint="10 characters allowed"
            maxLength={10}
            value={value}
            onChange={({ target: { value: v } }) => {
              const val = v === '' || v == null ? '' : Number(v);
              onChange(val);
            }}
          />
        )}
      />
    );
  };

  const renderToolTip = () => {
    const getToolTipContent = () => (
      <div className="padding-1 radius-lg">
        <h2 className="text-black font-sans-lg margin-0">
          <Icon
            iconName="info"
            className="usa-icon--size-3 text-baseline"
            style={{ fill: 'black' }}
          />
          <span className="margin-left-1">Create new unit types</span>
        </h2>

        <p className="font-sans-lg margin-0">
          New unit types can be created <br /> in the
          <a
            href="/catalog/codes?filter-category=Units"
            className="text-underline margin-left-1"
            style={{ cursor: 'pointer' }}
          >
            Catalog codes table
          </a>
        </p>
      </div>
    );
    return (
      <ReactTooltip
        id="unit-tooltip"
        textColor="black"
        delayHide={100}
        clickable
        backgroundColor="#FAF3D1"
        getContent={getToolTipContent}
      />
    );
  };

  const defaultEqAssValue =
    equipmentAssociation?.inputTypeCode !== undefined &&
    equipmentAssociation?.inputTypeCode !== null &&
    equipmentAssociation?.inputTypeCode !== ''
      ? equipmentAssociation?.inputTypeCode
      : '';

  const renderUnitDropdown = () => {
    return (
      <div className="grid-col-3">
        <h2
          className={`font-sans-md ${
            isNumerical ? '' : 'padding-bottom-3 margin-bottom-0'
          }`}
        >
          <div>
            Unit <span style={{ color: 'red' }}>*</span>
            <Icon
              iconName="info"
              className="font-sans-md margin-bottom-0 margin-left-05 padding-0"
              size="2"
              style={{ fill: '#005EA2' }}
              data-tip=""
              data-for="unit-tooltip"
            />
          </div>
        </h2>
        {renderToolTip()}
        <Controller
          id="si_edit_unit"
          name="unitCode"
          control={control}
          defaultValue={equipmentAssociation?.unitCode ?? ''}
          render={({ field: { onChange, value } }) => (
            <SelectDropdown
              id="si_edit_unit_sel"
              data-testid="minimum-unit-field"
              aria-label="unit"
              disabled={
                !isNew && isStandardItemInBetweenBidPeriods && isUnitCodeSet()
              }
              value={value || ''}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              options={getMultipleOptions('standardsCodeModel', 'units')}
            />
          )}
        />
        {errors?.unitCode?.message && (
          <ErrorMessage>{errors.unitCode.message}</ErrorMessage>
        )}
      </div>
    );
  };

  // Renders the predefined values table section
  const renderPredefinedValuesSection = () => {
    return (
      <Controller
        name="preDefinedValue"
        defaultValue={
          isEmpty(predefinedValues)
            ? (equipmentAssociation?.preDefinedValue?.value ?? [])
            : predefinedValues
        }
        control={control}
        render={({ field: { onChange, value } }) => (
          <PredefinedValueTable
            value={
              value.length > 0
                ? value.filter((r) => !deletedRows.includes(r))
                : value
            }
            errorMessage={errors?.preDefinedValue?.message}
            predefinedValues={predefinedValues}
            setPredefinedValues={setPredefinedValues}
            deletedRows={deletedRows}
            showAdd={showAdd}
            setShowAdd={setShowAdd}
            setDeletedRows={setDeletedRows}
            showEdit={showEdit}
            setShowEdit={setShowEdit}
            setNewRows={setNewRows}
            newRows={newRows}
            onChange={(v) => {
              const newValues = isEmpty(v)
                ? v
                : v.filter((r) => !deletedRows.includes(r));
              onChange({ value: newValues });
            }}
          />
        )}
      />
    );
  };

  // Renders the numerical input fields (with criteria, unit, and value)
  const renderNumericalFields = () => {
    const shouldShowNumericalValue =
      criteriaValue !== '' && criteriaValue !== 'ANY';
    return (
      <div className="grid-row padding-bottom-2 grid-gap-2">
        {/* Criteria */}
        <div className="grid-col-3">
          {renderRequiredComponent('Criteria')}
          <Controller
            id="si_edit_criteria"
            name="criteriaCode"
            control={control}
            defaultValue={equipmentAssociation?.criteriaCode ?? ''}
            render={({ field: { onChange, value } }) => (
              <SelectDropdown
                id="si_edit_criteria_sel"
                data-testid="minimum-criteria-field"
                aria-label="criteria"
                onChange={(selected) => {
                  setCriteriaValue(selected.target.value);
                  onChange(selected);
                }}
                options={getMultipleOptions('standardsCodeModel', 'criteria')}
                value={value}
              />
            )}
          />
          {errors?.criteriaCode?.message && (
            <ErrorMessage>{errors.criteriaCode.message}</ErrorMessage>
          )}
        </div>

        {/* Unit */}
        {renderUnitDropdown()}

        {/* Numerical Value - Hidden when criteria is '' or 'ANY' */}
        {shouldShowNumericalValue && (
          <div className="grid-col-6">
            {renderRequiredComponent('Numerical value')}
            {renderBoundValue(
              'lowerBound',
              equipmentAssociation?.lowerBound ?? 0,
            )}
            {errors?.lowerBound?.message && (
              <ErrorMessage>{errors.lowerBound.message}</ErrorMessage>
            )}
          </div>
        )}
      </div>
    );
  };

  //Renders the numerical range input fields (lower and upper bounds)
  const renderNumericalRangeFields = () => {
    return (
      <div className="grid-row padding-bottom-2 grid-gap-2">
        {/* Lower Bound */}
        <div className="grid-col-4">
          {renderRequiredComponent('Numerical value 1', 'margin-bottom-0')}
          <p className="text-gray-50 margin-0">
            Must be greater than or equal to X
          </p>
          {renderBoundValue(
            'lowerBound',
            equipmentAssociation?.lowerBound ?? 0,
          )}
          {errors?.lowerBound?.message && (
            <ErrorMessage>{errors.lowerBound.message}</ErrorMessage>
          )}
        </div>

        {/* Upper Bound */}
        <div className="grid-col-4">
          {renderRequiredComponent('Numerical value 2', 'margin-bottom-0')}
          <p className="text-gray-50 margin-0">
            Must be less than or equal to Y
          </p>
          {renderBoundValue(
            'upperBound',
            equipmentAssociation?.upperBound ?? 0,
          )}
          {errors?.upperBound?.message && (
            <ErrorMessage>{errors.upperBound.message}</ErrorMessage>
          )}
        </div>

        {/* Unit */}
        {renderUnitDropdown()}
      </div>
    );
  };

  /**
   * Renders the specification fields section for Standard association only:
   * - Standard details text input
   * - Input type dropdown
   * - Numerical, range, or predefined values input fields if type is selected
   */
  const renderSpecificationFields = () => {
    return (
      <>
        {/* Standard details */}
        <div className="grid-row padding-bottom-2">
          <div className="grid-col-6">
            <h2 className="font-sans-md">Standard details</h2>
            <Controller
              id="additional-info-text"
              name="associationText"
              defaultValue={equipmentAssociation?.associationText ?? ''}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextInput
                  data-testid="association-add-info-field"
                  aria-label="Standard details text input 80 characters allowed"
                  type="textarea"
                  value={value}
                  maxLength={80}
                  name="associationText"
                  error={errors?.associationText}
                  onChange={(e) => onChange(e.target.value)}
                  hint="80 characters allowed"
                />
              )}
            />
            {errors?.associationText?.message && (
              <ErrorMessage>{errors.associationText.message}</ErrorMessage>
            )}
          </div>
        </div>

        {/* Input Type Dropdown */}
        <div className="grid-row padding-bottom-2 grid-gap-3 display-flex flex-row">
          <div className="grid-col-6">
            {renderRequiredComponent('Minimums')}
            <Controller
              name="inputTypeCode"
              defaultValue={defaultEqAssValue}
              control={control}
              render={({ field: { onChange, value } }) => (
                <SelectDropdown
                  data-testid="association-input-type-field"
                  aria-label="Input type"
                  aria-required="true"
                  value={value}
                  options={getMinimumOptions()}
                  onChange={(e) => {
                    const v = e.target.value;
                    setIsNumerical(v === 'N');
                    setIsNumericalRange(v === 'R');
                    setIsPredefinedValues(v === 'P');
                    onChange(v);
                  }}
                />
              )}
            />
            {errors?.inputTypeCode?.message && (
              <ErrorMessage>{errors.inputTypeCode?.message}</ErrorMessage>
            )}
          </div>
        </div>

        {/* Render input type-specific fields */}
        {isNumerical && renderNumericalFields()}
        {isNumericalRange && renderNumericalRangeFields()}
        {isPredefinedValues && renderPredefinedValuesSection()}
      </>
    );
  };

  return (
    <>
      {/* Association Type */}
      <div className="grid-row padding-bottom-2">
        <div className="grid-col-3">
          {renderRequiredComponent('Type')}
          <Controller
            id="si_edit_type"
            name="associationTypeCode"
            control={control}
            defaultValue={equipmentAssociation?.associationTypeCode ?? ''}
            render={({ field: { onChange, value } }) => (
              <SelectDropdown
                id="si_edit_type_sel"
                data-testid="association-type-field"
                aria-label="Association Type"
                aria-required="true"
                onChange={(v) => {
                  setShowSpec(v.target.value === 'S');
                  onChange(v);
                }}
                options={getMultipleOptions(
                  'standardsCodeModel',
                  'associationType',
                )}
                value={value}
                readOnly
                disabled={!isNew && (canPartialEdit || isInContractYear)}
              />
            )}
          />
          {errors?.associationTypeCode?.message && (
            <ErrorMessage>{errors.associationTypeCode.message}</ErrorMessage>
          )}
        </div>
      </div>

      {/* Specification Fields - Only shown when showSpec is true */}
      {showSpec && renderSpecificationFields()}
    </>
  );
};

AssociationSpecificationFields.propTypes = {
  control: PropTypes.shape(Object).isRequired,
  errors: PropTypes.shape(Object).isRequired,
  setValue: PropTypes.func.isRequired,
  equipmentAssociation: PropTypes.shape(Object),
  multipleOptions: PropTypes.arrayOf(Object),
  isStandardItemInBetweenBidPeriods: PropTypes.bool,
  canPartialEdit: PropTypes.bool,
  isInContractYear: PropTypes.bool,
  isNew: PropTypes.bool,
};

export default AssociationSpecificationFields;
